import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase, withTimeout } from "@/lib/hooks/api/utils";
import type { Ticket, TicketPriority, TicketStatus } from "@/lib/hooks/api/types";
import { UpdateTicketPriorityContext, UpdateTicketStatusContext } from "./tickets-types";

export function useUpdateTicketStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, status }: { ticketId: string; status: TicketStatus }) => {
      const supabase = getSupabase();
      const { data, error } = await withTimeout(
        supabase.from("tickets").update({ status }).eq("id", ticketId).select().single(),
        15000,
        "update ticket status"
      );

      if (error) {
        throw new Error(error.message || "Failed to update ticket status");
      }

      return data;
    },
    onMutate: async ({ ticketId, status }): Promise<UpdateTicketStatusContext> => {
      await queryClient.cancelQueries({ queryKey: ["ticket", ticketId] });

      const previousTicket = queryClient.getQueryData<Ticket>(["ticket", ticketId]);

      if (previousTicket) {
        queryClient.setQueryData<Ticket>(["ticket", ticketId], {
          ...previousTicket,
          status,
        });
      }

      return { previousTicket };
    },
    onError: (err, { ticketId }, onMutateResult: UpdateTicketStatusContext | undefined) => {
      if (onMutateResult?.previousTicket) {
        queryClient.setQueryData<Ticket>(["ticket", ticketId], onMutateResult.previousTicket);
      }
      toast.error("Failed to update ticket status");
      console.error("Error updating ticket status:", err);
    },
    onSuccess: () => {
      toast.success("Ticket status updated successfully");
    },
    onSettled: (data, error, { ticketId }) => {
      void queryClient.invalidateQueries({ queryKey: ["ticket", ticketId] });
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
    },
  });
}

export function useUpdateTicketPriority() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, priority }: { ticketId: string; priority: TicketPriority }) => {
      const supabase = getSupabase();
      const { data, error } = await withTimeout(
        supabase.from("tickets").update({ priority }).eq("id", ticketId).select().single(),
        15000,
        "update ticket priority"
      );

      if (error) {
        throw new Error(error.message || "Failed to update ticket priority");
      }

      return data;
    },
    onMutate: async ({ ticketId, priority }): Promise<UpdateTicketPriorityContext> => {
      await queryClient.cancelQueries({ queryKey: ["ticket", ticketId] });

      const previousTicket = queryClient.getQueryData<Ticket>(["ticket", ticketId]);

      if (previousTicket) {
        queryClient.setQueryData<Ticket>(["ticket", ticketId], {
          ...previousTicket,
          priority,
        });
      }

      return { previousTicket };
    },
    onError: (err, { ticketId }, onMutateResult: UpdateTicketPriorityContext | undefined) => {
      if (onMutateResult?.previousTicket) {
        queryClient.setQueryData<Ticket>(["ticket", ticketId], onMutateResult.previousTicket);
      }
      toast.error("Failed to update ticket priority");
      console.error("Error updating ticket priority:", err);
    },
    onSuccess: () => {
      toast.success("Ticket priority updated successfully");
    },
    onSettled: (data, error, { ticketId }) => {
      void queryClient.invalidateQueries({ queryKey: ["ticket", ticketId] });
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
    },
  });
}

export function useUpdateTicketAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, assigned_to }: { id: string; assigned_to: string | null }) => {
      const supabase = getSupabase();
      const { data, error } = await withTimeout(
        supabase.from("tickets").update({ assigned_to }).eq("id", id).select().single(),
        15000,
        "update ticket assignment"
      );

      if (error) {
        throw new Error(error.message || "Failed to update ticket assignment");
      }

      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
    },
  });
}
